/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import useBaseService from '../../../hooks/services/useBaseService'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import { entities } from '../../../constants/entities'
import MainLayout from '../../../layouts/MainLayout'
import PageHeader from '../../../components/PageHeader'
import { faFileExcel, faFilePdf } from '@fortawesome/free-solid-svg-icons'
import useForm from '../../../hooks/useForm'
import { cleanPayload, setDefaultFieldValues } from '../../../utils/objectUtils'
import SimpleBody from '../../../components/forms/SimpleBody'
import SectionHeader from '../../../components/forms/SectionHeader'
import Form from '../../../components/forms/Form'
import { filterFields } from './filters'
import useRemoteSelectOptions from '../../../hooks/useRemoteSelectOptions'
import Button from '../../../components/Button'
import { Alert, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useOrgan } from '../../../hooks/useOrgan'
import useSelectOptions from '../../../hooks/useSelectOptions'
import { useReport } from '../../../context/ReportContext'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'

const LogisticsReport = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedOrgan } = useOrgan()
  const { setExportId, isExporting } = useReport()

  const [filtersSection] = filterFields
  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues([filtersSection]),
    [filtersSection]
  )

  const { get: baseServiceGet } = useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  const docType = formData.filter.document_type?.value
  const selectedCustomerId = formData.filter.customer_id?.value ?? formData.filter.customer_id ?? null

  const documentTypeSelect = useSelectOptions({
    fieldId: 'filter.document_type',
    formData,
    setFormData,
    rawOptions: [
      { value: 'movement', label: 'Movimentos' },
      { value: 'order', label: 'Requisições' },
      { value: 'shipment', label: 'Carregamentos' },
    ],
    mapToOption: (item) => ({ value: item.value, label: item.label }),
  })

  const customersSelect = useRemoteSelectOptions({
    queryKey: ['customers'],
    entityPath: entities.customers.get,
    baseServiceGet,
    fieldId: 'filter.customer_id',
    formData,
    setFormData,
    perPage: 15,
    enablePagination: true,
    mapToOption: (item) => ({ value: item.id, label: item.name }),
  })

  const contractsSelect = useRemoteSelectOptions({
    queryKey: ['contractsSelect', selectedCustomerId],
    entityPath: entities.contracts.forSelect,
    baseServiceGet,
    fieldId: 'filter.contract_id',
    formData,
    setFormData,
    enabled: Boolean(selectedCustomerId),
    extraFilters: selectedCustomerId
      ? { 'recipientCustomers.customer_id': { $eq: selectedCustomerId } }
      : {},
    perPage: 15,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.value ?? item.id,
      label: item.label ?? item.name,
    }),
  })

  const handleFilterChange = useCallback((fieldId, value) => {
    handleChange(fieldId, value)
    if (fieldId === 'filter.customer_id') {
      setFormData((prev) => ({
        ...prev,
        filter: { ...prev.filter, customer_id: value, contract_id: null },
      }))
    }
  }, [handleChange, setFormData])

  useEffect(() => {
    setFormData((prev) => ({
        ...prev,
        filter: { ...prev.filter, status_id: null },
    }))
    }, [docType])

  const statusEndpoint = useMemo(() => {
  if (docType === 'movement') return entities.movementStatus.get
  if (docType === 'order') return entities.serviceOrderStatus.get
  if (docType === 'shipment') return entities.shipmentStatus.get

  return null
}, [docType])

const statusSelect = useRemoteSelectOptions({
  queryKey: ['logisticsStatus', docType],
  fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
    if (!statusEndpoint) {
      return {
        data: [],
        current_page: 1,
        last_page: 1,
        total: 0,
      }
    }

    const filters = transformFiltersForUrl({
      deleted_at: false,
      ...(search && { name: search }),
    })

    const params = {
      filters,
      page,
      perPage,
    }

    const response = await baseServiceGet(statusEndpoint, params)
    const result = response.result

    /**
     * Compatibilidade:
     * Alguns endpoints retornam:
     * - result.data
     * Outros retornam:
     * - result direto como array
     */
    if (Array.isArray(result)) {
      return {
        data: result,
        current_page: 1,
        last_page: 1,
        total: result.length,
      }
    }

    return result
  },
  fieldId: 'filter.status_id',
  formData,
  setFormData,
  perPage: 15,
  initialPage: 1,
  enablePagination: true,
  mapToOption: (item) => ({
    value: item.id,
    label: item.name,
  }),
})

  const selects = {
    'filter.document_type': documentTypeSelect,
    'filter.status_id': statusSelect,
    'filter.customer_id': customersSelect,
    'filter.contract_id': contractsSelect,
  }

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}
      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback((fieldId) => selects[fieldId]?.options || [], [selects])
  const getSelectedValue = useCallback((fieldId) => selects[fieldId]?.selectedValue || null, [selects])

  const exportReport = useMutation({
    mutationFn: async (type) => {
      if (!docType) {
        showNotification('warning', 'Selecione o tipo de documento antes de exportar.')
        return
      }
      showLoader()
      const formated = cleanPayload(formData.filter)
      const params = {
        filters: {
          document_type: formated.document_type?.value ?? formated.document_type,
          ...(formated.start_date && { start_date: formated.start_date }),
          ...(formated.end_date && { end_date: formated.end_date }),
          ...(formated.status_id && { status_id: formated.status_id }),
          ...(formated.customer_id && { customer_id: formated.customer_id }),
          ...(formated.contract_id && { contract_id: formated.contract_id }),
          organization_id: selectedOrgan?.id,
        },
      }
      const response = await baseServiceGet(entities.reports.logistics.get, params, { type })
      return response
    },
    onSuccess: (response) => {
      if (!response) return
      if (response?.status === 202 || response?.status === 200) {
        const generated_id = response.result?.export_id
        if (generated_id) {
          setExportId(generated_id)
          localStorage.setItem('report_exportId', generated_id.toString())
          showNotification('info', 'Processamento do relatório iniciado em segundo plano.')
        }
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      hideLoader()
    },
    onSettled: () => hideLoader(),
  })

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Relatório Logístico" showBackButton={false} />
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Atenção:</strong> Este relatório apresenta movimentos, requisições e carregamentos conforme os filtros selecionados.
      </Alert>
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={() => {}}
          textSubmit="Filtrar"
          textLoadingSubmit="Filtrando..."
          showButtons={false}
          disabledSubmitButton={true}>
          {() => (
            <>
              <div key={filtersSection.section}>
                <SectionHeader section={filtersSection.section} />

                <SimpleBody
                  fields={filtersSection.fields.map((field) =>
                    field.id === 'filter.contract_id'
                      ? { ...field, disabled: !selectedCustomerId }
                      : field
                  )}
                  formErrors={{}}
                  formData={formData}
                  handleFieldChange={handleFilterChange}
                  getOptions={getOptions}
                  getSelectedValue={getSelectedValue}
                  getSelectMeta={getSelectMeta}
                />
              </div>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  my: 4,
                  width: '100%',
                  gap: 2,
                }}>
                <Button
                  variant="outlined"
                  color="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFilePdf} />}
                  text="Exportar em PDF"
                  disabled={isExporting || !docType}
                  onClick={async () => {
                    try {
                      await exportReport.mutateAsync('pdf')
                    } catch (error) {
                      console.error('Erro ao exportar PDF:', {
                        status: error.status,
                        message: error.message,
                      })
                    }
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFileExcel} />}
                  text="Exportar em EXCEL"
                  disabled={isExporting || !docType}
                  onClick={async () => {
                    try {
                      await exportReport.mutateAsync('excel')
                    } catch (error) {
                      console.error('Erro ao exportar EXCEL:', {
                        status: error.status,
                        message: error.message,
                      })
                    }
                  }}
                />
              </Box>
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  )
}

export default LogisticsReport
